import { useEffect, useState } from "react";
import { KeyRound, Search, ShieldAlert, Copy, CheckCircle2, Loader2, User, Lock, Unlock, AlertTriangle, Activity, ExternalLink, RefreshCw } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

/**
 * Admin fallback for password recovery when the email flow doesn't reach the
 * customer (Hotmail greylist, bad email in Arcos, customer lost access, etc.).
 *
 * Two actions:
 *   1. Look up the current password/lock status of a client by NIT.
 *   2. Generate a fresh 8-char temporary password and copy it to clipboard so
 *      the admin can communicate it to the customer verbally.
 */
export default function AdminClientPasswords() {
  const [nit, setNit] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  const [copied, setCopied] = useState(false);
  const [arcosDebug, setArcosDebug] = useState(null);
  const [debugging, setDebugging] = useState(false);

  const runArcosDebug = async () => {
    const n = nit.trim();
    if (!n) return toast.error("Ingresa un NIT");
    setDebugging(true);
    setArcosDebug(null);
    try {
      const { data } = await adminApi.get(`/admin/clients/${encodeURIComponent(n)}/arcos-debug`);
      setArcosDebug(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo ejecutar el diagnóstico");
    } finally {
      setDebugging(false);
    }
  };

  const query = async () => {
    const n = nit.trim();
    if (!n) return toast.error("Ingresa un NIT");
    setLoading(true);
    setStatus(null);
    setTempPwd(null);
    try {
      const { data } = await adminApi.get(`/admin/clients/${encodeURIComponent(n)}/password-status`);
      setStatus(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo consultar el estado");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    if (!status?.nit) return;
    if (!window.confirm(
      `¿Confirmas resetear la contraseña de ${status.razon_social || status.nit}?\n\n` +
      `Se generará una clave temporal que el cliente deberá cambiar en su primer ingreso. ` +
      `La contraseña actual dejará de funcionar.`
    )) return;
    setResetting(true);
    setTempPwd(null);
    try {
      const { data } = await adminApi.post(`/admin/clients/${encodeURIComponent(status.nit)}/reset-password`);
      setTempPwd(data);
      toast.success("Contraseña temporal generada");
      // Refresh status
      const { data: newStatus } = await adminApi.get(`/admin/clients/${encodeURIComponent(status.nit)}/password-status`);
      setStatus(newStatus);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar la contraseña");
    } finally {
      setResetting(false);
    }
  };

  const copyPwd = async () => {
    if (!tempPwd?.temp_password) return;
    try {
      await navigator.clipboard.writeText(tempPwd.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar. Selecciona manualmente");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-6 w-6 text-cm-red" />
          <h1 className="font-serif text-2xl">Reset de contraseña · Cliente</h1>
        </div>
        <p className="text-sm text-cm-ink-soft">
          Usa esta herramienta cuando el correo de recuperación no llega al cliente (Hotmail suele demorar).
          Comunica la clave temporal por WhatsApp o llamada.
        </p>
      </div>

      {/* NIT search */}
      <div className="bg-white border border-cm-border rounded-lg p-5 mb-4">
        <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-2">
          NIT del cliente
        </label>
        <div className="flex gap-2">
          <Input
            value={nit}
            onChange={(e) => setNit(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && query()}
            placeholder="Ej: 1047366647"
            data-testid="admin-pwd-nit-input"
            className="border-cm-border font-mono"
          />
          <Button
            onClick={query}
            disabled={loading || !nit.trim()}
            className="btn-cm-primary"
            data-testid="admin-pwd-search-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
          </Button>
        </div>
        <div className="mt-3 pt-3 border-t border-cm-border/60">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-cm-ink-soft">
              ¿El cliente ve <strong>"Cliente no encontrado"</strong> al entrar? Revisa qué devuelve Arcos para su NIT.
            </p>
            <Button
              onClick={runArcosDebug}
              disabled={debugging || !nit.trim()}
              variant="outline"
              size="sm"
              className="border-cm-border"
              data-testid="admin-arcos-debug-btn"
            >
              {debugging
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Consultando Arcos...</>
                : <><Activity className="h-4 w-4 mr-1" /> Diagnóstico Arcos</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Arcos diagnostic result */}
      {arcosDebug && (
        <div className="bg-white border border-cm-border rounded-lg p-5 mb-4" data-testid="admin-arcos-debug-result">
          <h3 className="font-serif text-lg mb-2 flex items-center gap-2">
            <Activity className="h-5 w-5 text-cm-ink-soft" /> Diagnóstico Arcos · NIT {arcosDebug.nit}
          </h3>
          <div className={`rounded p-3 mb-3 text-sm ${
            arcosDebug.total_found > 0
              ? "bg-green-50 border border-green-200"
              : "bg-amber-50 border border-amber-200"
          }`}>
            {arcosDebug.diagnosis}
          </div>
          <div className="space-y-2">
            {arcosDebug.attempts?.map((a, i) => (
              <div key={i} className="border border-cm-border rounded p-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono">Consulta Nit=<strong>{a.query_nit}</strong></span>
                  {a.error
                    ? <span className="text-red-600 font-medium">ERROR</span>
                    : <span className="text-cm-ink-soft">{a.count} registro(s)</span>}
                </div>
                {a.error && <p className="text-red-600 font-mono break-all">{a.error}</p>}
                {a.records?.map((r, j) => (
                  <div key={j} className="font-mono text-[11px] text-cm-ink-soft border-t border-cm-border/40 pt-1 mt-1">
                    id={r.ClienteId} · nit={r.ClienteNit} · estado={r.estado} · lista={r.lista_precios_id} · {r.nombre}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status card */}
      {status && (
        <div className="bg-white border border-cm-border rounded-lg p-5 mb-4" data-testid="admin-pwd-status">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium">Cliente</p>
              <h2 className="font-serif text-xl mt-0.5">
                {status.razon_social || `NIT ${status.nit}`}
              </h2>
              <div className="flex items-center gap-3 mt-1 text-xs text-cm-ink-soft">
                <span className="font-mono">NIT {status.nit}</span>
                {status.email && (
                  <>
                    <span>·</span>
                    <span>{status.email}</span>
                  </>
                )}
              </div>
              {!status.exists_in_arcos && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> No existe en Arcos
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <StatusCell
              icon={status.has_personal_password ? Lock : User}
              label="Contraseña"
              value={status.has_personal_password ? "Personal (cambiada)" : "Genérica (CM2026)"}
              tone={status.has_personal_password ? "ok" : "warn"}
            />
            <StatusCell
              icon={status.is_locked ? Lock : Unlock}
              label="Bloqueo por intentos"
              value={status.is_locked ? "BLOQUEADO" : "Desbloqueado"}
              tone={status.is_locked ? "danger" : "ok"}
            />
            {status.updated_at && (
              <StatusCell
                icon={KeyRound}
                label="Última actualización"
                value={new Date(status.updated_at).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}
              />
            )}
            <StatusCell
              icon={KeyRound}
              label="Solicitudes de reset"
              value={`${status.n_reset_tokens} link(s) generados`}
            />
          </div>

          <Button
            onClick={doReset}
            disabled={resetting || !status.exists_in_arcos}
            className="w-full bg-cm-red text-white hover:bg-cm-red/90"
            data-testid="admin-pwd-reset-btn"
          >
            {resetting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando...</>
            ) : (
              <><ShieldAlert className="h-4 w-4 mr-2" /> Generar contraseña temporal</>
            )}
          </Button>
        </div>
      )}

      {/* Generated password */}
      {tempPwd && (
        <div className="bg-cm-yellow/15 border-2 border-cm-yellow rounded-lg p-5 mb-6" data-testid="admin-pwd-temp-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-700" />
            <h3 className="font-serif text-lg">Contraseña temporal generada</h3>
          </div>
          <div className="bg-white border border-cm-border rounded p-4 mb-3">
            <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft mb-1">Comunícala al cliente</p>
            <div className="flex items-center gap-3">
              <code
                className="font-mono text-3xl font-bold tracking-wider text-cm-ink flex-1"
                data-testid="admin-pwd-value"
              >
                {tempPwd.temp_password}
              </code>
              <Button
                onClick={copyPwd}
                variant="outline"
                size="sm"
                className="border-cm-border"
                data-testid="admin-pwd-copy-btn"
              >
                {copied ? (
                  <><CheckCircle2 className="h-4 w-4 mr-1 text-green-700" /> Copiado</>
                ) : (
                  <><Copy className="h-4 w-4 mr-1" /> Copiar</>
                )}
              </Button>
            </div>
          </div>
          <ul className="text-xs text-cm-ink-soft space-y-1 list-disc pl-4">
            <li>El cliente puede ingresar de inmediato — el bloqueo por intentos fallidos ya se limpió.</li>
            <li>Al iniciar sesión con esta clave, se le mostrará el modal para crear una definitiva.</li>
            <li>Esta contraseña se muestra una sola vez. Compártela por un canal seguro (WhatsApp, llamada).</li>
            <li>La contraseña anterior del cliente ya no funciona.</li>
          </ul>
        </div>
      )}

      {/* Recent email attempts / diagnostic */}
      <RecentResetAttempts />
    </div>
  );
}

const RecentResetAttempts = () => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/email/recent-attempts?limit=20");
      setItems(data.items || []);
    } catch (err) {
      toast.error("No pude cargar los intentos recientes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (items === null && !loading) return null;

  return (
    <div className="bg-white border border-cm-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-cm-ink-soft" />
          <h3 className="font-serif text-lg">Últimos correos de reset enviados</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="reload-attempts-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <p className="text-xs text-cm-ink-soft mb-3">
        Cuando un cliente reporte que no llegó el correo, busca su NIT aquí. Si <strong>resend_id</strong> está presente = Resend aceptó
        el envío (revisa spam/promociones del cliente o abre el ID en tu dashboard de Resend). Si dice <strong>resend_error</strong>, ahí está la falla.
      </p>
      {loading && (
        <div className="text-center py-6 text-cm-ink-soft text-sm">Cargando...</div>
      )}
      {items && items.length === 0 && (
        <div className="text-center py-6 text-cm-ink-soft text-sm">Sin intentos registrados</div>
      )}
      {items && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft border-b border-cm-border">
                <th className="pb-2 pr-3">Fecha</th>
                <th className="pb-2 pr-3">NIT</th>
                <th className="pb-2 pr-3">Correo</th>
                <th className="pb-2 pr-3">Estado</th>
                <th className="pb-2 pr-3">Resend ID / Error</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-b border-cm-border/50 last:border-b-0">
                  <td className="py-2 pr-3 font-mono text-[11px]">
                    {new Date(it.created_at).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="py-2 pr-3 font-mono">{it.nit}</td>
                  <td className="py-2 pr-3">{it.email_masked}</td>
                  <td className="py-2 pr-3">
                    {it.used_at ? (
                      <span className="text-green-700">✓ Usado</span>
                    ) : it.sent_ok ? (
                      <span className="text-cm-ink-soft">Enviado</span>
                    ) : (
                      <span className="text-red-600">Falló</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px]">
                    {it.resend_id ? (
                      <a
                        href={`https://resend.com/emails/${it.resend_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        {it.resend_id.substring(0, 12)}...
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-red-600" title={it.resend_error || ""}>
                        {it.resend_error ? it.resend_error.substring(0, 50) + "..." : "—"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatusCell = ({ icon: Icon, label, value, tone = "neutral" }) => {
  const toneClass = {
    ok: "border-green-200 bg-green-50",
    warn: "border-amber-200 bg-amber-50",
    danger: "border-red-200 bg-red-50",
    neutral: "border-cm-border bg-cm-cream-alt/40",
  }[tone];
  const iconTone = {
    ok: "text-green-700",
    warn: "text-amber-700",
    danger: "text-red-700",
    neutral: "text-cm-ink-soft",
  }[tone];
  return (
    <div className={`border rounded p-3 ${toneClass}`}>
      <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium mb-1 flex items-center gap-1.5">
        <Icon className={`h-3 w-3 ${iconTone}`} /> {label}
      </p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
};
